import { useHead, useRoute } from '#imports';

interface PageData {
  title?: string;
  description?: string;
  ogImage?: {
    _ref: string;
  };
  author?: string;
  authorImage?: {
    asset?: {
      _ref: string;
    };
  };
  dateModified?: string;
  datePublished?: string;
  _id?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  excludeFromExternalSearch?: boolean;
  structuredData?: StructuredDataOptions[];
}

export function usePageHead(props: PageData) {
  const route = useRoute();
  const baseUrl = process.env.NUXT_BASE_URL || 'https://www.onshape.com';
  // Language comes from the dynamic `[lang]` route segment when present.
  const routeLanguage =
    typeof route.params.lang === 'string' ? route.params.lang : route.params.lang?.[0];
  const fullUrl = new URL(route.fullPath, baseUrl).toString();
  const canonicalUrl = replaceLanguageInUrl(fullUrl, routeLanguage || 'en', {
    insertWhenMissing: Boolean(routeLanguage),
  });
  const resolvedTitle = resolvePageTitle(props.title ?? '');
  const ogImageUrl = props.ogImage ? cdnImage(props.ogImage._ref).asIs : '';
  const defaultTitle = 'Onshape | Product Development Platform';
  const defaultDescription =
    'Onshape Inc. develops the only product design platform that combines 3D CAD, PDM, collaboration and analytics tools in the cloud.';
  const defaultAuthor = STRUCTURED_DATA_DEFAULTS.organization.name;
  const { $staticImages } = useNuxtApp();
  const appleTouchImageSrc = $staticImages['apple-touch-icon'];

  useHead({
    title: resolvedTitle,
    meta: [
      {
        property: 'og:title',
        content: props.title ?? defaultTitle,
      },
      {
        name: 'twitter:title',
        content: props.title ?? defaultTitle,
      },
      {
        name: 'description',
        content: props.description ?? defaultDescription,
      },
      {
        property: 'og:description',
        content: props.description ?? defaultDescription,
      },
      {
        name: 'twitter:description',
        content: props.description ?? defaultDescription,
      },
      { name: 'twitter:card', content: props.twitterCard ?? 'summary_large_image' },
      {
        property: 'og:image',
        content: ogImageUrl,
      },
      { name: 'referrer', content: 'no-referrer-when-downgrade' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: fullUrl },
      {
        name: 'author',
        content: props.author ?? defaultAuthor,
      },
      {
        name: 'asset_id',
        content: props._id ?? '',
      },
      {
        name: 'asset_label',
        content: props.title ?? '',
      },
      {
        name: 'languages',
        content: 'en',
      },
      {
        name: 'robots',
        content:
          props.excludeFromExternalSearch ? 'noindex, nofollow' : (
            'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
          ),
      },
      {
        name: 'facebook-domain-verification',
        content: process.env.FACEBOOK_DOMAIN_VERIFICATION,
      },
    ].filter((meta) => !!meta.content),
    link: [
      {
        rel: 'canonical',
        href: canonicalUrl,
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: appleTouchImageSrc,
      },
    ],
  });

  if (props.structuredData) {
    const buildStructuredDataScript = (
      entry: StructuredDataOptions,
      index: number
    ): StructuredDataScript | undefined => {
      // Provide page-level defaults so each structured data entry can stay minimal.
      const structuredDataOptions: StructuredDataOptions = {
        ...entry,
        canonicalUrl: entry.canonicalUrl ?? canonicalUrl,
        imageUrl: entry.imageUrl ?? ogImageUrl,
      };
      const structuredData = structuredDataBuilder(structuredDataOptions);
      if (!structuredData) {
        return;
      }

      return {
        key: `structured-data-${structuredDataOptions.schemaType.toLowerCase()}-${index}`,
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(structuredData),
      };
    };

    const scripts = props.structuredData
      .map((entry, index) => buildStructuredDataScript(entry, index))
      .filter(isDefined);

    if (scripts.length) {
      // Inject one JSON-LD script per valid entry to preserve schema granularity.
      useHead({
        script: scripts,
      });
    }
  }
}
